
import { defineComponent } from 'vue'

import { Context } from '@nuxt/types'
import { MetaInfo } from 'vue-meta'

import { handleError } from '@/plugins/helper'
import AuroraBackground from '~/components/common/aurora-background.vue'
import PageBottom from '~/components/common/page-bottom/index.vue'
import TextPressure from '~/components/common/text-pressure.vue'
import FooterNew from '~/components/FooterNew.vue'
import TextPressureMixin from '~/mixins/text-pressure.vue'
import ArticleFilter from '~/sections/article/filter.vue'
import ArticleKv from '~/sections/article/kv.vue'
import ArticleList from '~/sections/article/list.vue'
import ArticlePagination from '~/sections/article/pagination.vue'
// import ArticleSearch from '~/sections/article/search.vue'

import { ArticleFilterNameType, ArticleFilterType, ArticleType } from '@/models/'

const ITEM_PER_PAGE = 24

function normalize(val: unknown): string[] {
  if (typeof val === 'string') {
    return val
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
  }
  return []
}

function buildQueryString(query: Record<string, unknown>): string {
  return Object.entries(query)
    .map(([key, val]) => {
      if (Array.isArray(val)) {
        return val
          .filter((v): v is string => typeof v === 'string')
          .map((v) => `${key}=${encodeURIComponent(v)}`)
          .join('&')
      } else if (typeof val === 'string') {
        return `${key}=${encodeURIComponent(val)}`
      } else {
        return ''
      }
    })
    .filter(Boolean)
    .join('&')
}

function matchFilter<T>(filter: T[], value: T | T[]): boolean {
  if (filter.length === 0) {
    return true
  }
  if (Array.isArray(value)) {
    return filter.some((f) => value.includes(f))
  }
  return filter.includes(value)
}

export default defineComponent({
  components: {
    TextPressure,
    PageBottom,
    FooterNew,
    AuroraBackground,
    ArticleKv,
    ArticleFilter,
    ArticleList,
    ArticlePagination
  },
  mixins: [TextPressureMixin],
  layout: 'home',
  async asyncData({ $content }: Context): Promise<object | void> {
    const dataFilter = await $content('article/all-filter')
      .fetch<ArticleFilterType>()
      .catch(handleError)

    return { dataFilter: dataFilter || {} as ArticleFilterType }
  },
  data() {
    const itemsPerPage = ITEM_PER_PAGE
    const articles: ArticleType[] = []
    const dataFilter = {} as ArticleFilterType
    const isShowTags: boolean = false
    const totalItems: number = 0
    const isLoading: boolean = true

    const pageQuery = parseInt(this.$route.query.page as string, 10)
    const currentPage = isNaN(pageQuery) || pageQuery < 1 ? 1 : pageQuery

    const { tag, category, type, media } = this.$route.query

    const queryFilter = {
      tag: normalize(tag),
      category: normalize(category),
      type: normalize(type),
      media: normalize(media)
    }

    return {
      itemsPerPage,
      articles,
      dataFilter,
      isShowTags,
      currentPage,
      queryFilter,
      totalItems,
      isLoading
    }
  },
  fetchOnServer: false,
  async fetch() {
    await this.fetchArticles()
  },
  head(): MetaInfo {
    const title = 'ARTICLE'
    const extendMeta = this.$helper.setMetaTitleAndDescription(title)

    return { title, meta: [...extendMeta] }
  },
  computed: {
    page(): number {
      return this.currentPage || 1
    },
    getFilteredArticles(): ArticleType[] {
      const { tag, category, type, media } = this.queryFilter
      return this.articles.filter((article) => {
        return (
          matchFilter(type, article.types) &&
          matchFilter(category, article.category) &&
          matchFilter(tag, article.tags) &&
          matchFilter(media, article.media)
        )
      })
    },
    totalPage() {
      return Math.max(1, Math.ceil((this.totalItems || 0) / this.itemsPerPage))
    },
    articlesDisplayed() {
      return this.articles
    }
  },
  watch: {
    queryFilter: {
      handler() {
        this.fetchArticles()
      },
      deep: true
    }
  },
  methods: {
    setRoute(q?: number | string, filterName?: ArticleFilterNameType): void {
      let query = this.$route.query
      const isChangePage = typeof q === 'number'

      if (!q) {
        query = { page: String(1) }
        this.queryFilter = { tag: [], category: [], type: [], media: [] }
        this.currentPage = 1
      } else if (isChangePage) {
        query = { ...query, page: String(q) }
        this.currentPage = q
      } else if (filterName) {
        const { type, category, tag, media } = this.queryFilter

        const filterMap: Record<ArticleFilterNameType, string[]> = { type, category, tag, media }
        const currentFilters = filterMap[filterName] || []
        const filterData = currentFilters.includes(q)
          ? currentFilters.filter((f) => f !== q)
          : [...currentFilters, q]

        if (filterData.length > 0) {
          query[filterName!] = filterData.join(',')
        } else {
          delete query[filterName!]
        }

        query = { ...query, page: String(1) }
        this.currentPage = 1
        this.queryFilter[filterName] = filterData
      }

      const queryString = buildQueryString(query)

      const newUrl = `${this.$route.path}?${queryString}`

      window.history.replaceState(null, '', newUrl)

      if (isChangePage) {
        // Fetch new page items when pagination changes
        this.fetchArticles()
        this.$scrollTo('.p-article__search-filter', 300, {
          easing: 'ease-in-out',
          offset: -100
        })
      }
    },
    async fetchArticles(): Promise<void> {
      this.isLoading = true
      const { $content } = this.$nuxt.context
      const { handleError } = this.$helper
      const { currentPage, itemsPerPage, queryFilter } = this

      const offset = (currentPage - 1) * itemsPerPage

      const { type, category, tag, media } = queryFilter
      const normalizeNumeric = (arr: string[]) => arr.map((v) => (/^\d+$/.test(v) ? Number(v) : v))
      const typeFilter = normalizeNumeric(type)
      const where: Record<string, unknown> = {}
      if (type.length) {
        where.types = { $containsAny: typeFilter }
      }
      if (category.length) {
        where.category = { $in: category }
      }
      if (tag.length) {
        where.tags = { $containsAny: tag }
      }
      if (media.length) {
        where.media = { $in: media }
      }

      let q = await $content('article/all')
        .limit(itemsPerPage)
        .skip(offset)
        .only(['url', 'thumbnail', 'thumbnailUrl', 'category', 'publicDateStr', 'title', 'media'])
      if (Object.keys(where).length) {
        q = q.where(where)
      }

      const pageItems = await q.fetch<ArticleType[]>().catch(handleError)
      this.articles =
        pageItems && Array.isArray(pageItems)
          ? (pageItems as ArticleType[])
          : [pageItems as unknown as ArticleType]

      // query total items
      let countQ = $content('article/all').only(['slug'])
      if (Object.keys(where).length) {
        countQ = countQ.where(where)
      }
      const total = await countQ.fetch().catch(handleError)
      this.totalItems = Array.isArray(total) ? total.length : 0
      this.isLoading = false
    }
  }
})
